import { useEffect, useMemo, useState } from 'react';
import { ecorpResources } from '@command-center/admin-ecorp';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  usePageHeader,
} from '@command-center/ui-system';
import {
  Activity as ActivityIcon,
  AlertTriangle,
  ArrowLeft,
  ClipboardCopy,
  FileStack,
  Info,
  Loader2,
  Map as MapIcon,
  Pencil,
  Save,
  Star,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  deleteRecord,
  getProjectBrief,
  getProvenance,
  getRecord,
  getSuggestions,
  listActivity,
  listRecords,
  updateRecord,
  type ActivityItem,
  type PortalRecord,
  type ProvenanceItem,
} from '../api';
import {
  display,
  EmptyState,
  ErrorNotice,
  linkedIds,
  recents,
  relativeTime,
  SectionHeader,
  stars,
  StatusPill,
  useToast,
} from '../lib';
import { FieldInput, RecordTable } from '../ResourceView';
import { DocumentDetail } from '../components/DocumentDetail';
import { UploadDialog } from '../components/UploadDialog';

const missingChecklist: Array<{ group: string; field: string; label: string }> = [
  { group: 'Project identity', field: 'Project Type', label: 'Project type' },
  { group: 'Client', field: 'Client', label: 'Linked client' },
  { group: 'Client', field: 'Primary Client Contact', label: 'Primary client contact' },
  { group: 'Site & parcels', field: 'Primary Project Address', label: 'Primary project address' },
  { group: 'Site & parcels', field: 'Assessor’s Parcel Numbers', label: 'Assessor parcel numbers' },
  { group: 'Site & parcels', field: 'Site Area — Acres', label: 'Site area' },
  { group: 'Site & parcels', field: 'Jurisdiction', label: 'Jurisdiction' },
  { group: 'Existing conditions', field: 'Existing Use', label: 'Existing use' },
  { group: 'Existing conditions', field: 'Existing Zoning', label: 'Existing zoning' },
  { group: 'Proposed development', field: 'Proposed Use', label: 'Proposed use' },
  {
    group: 'Proposed development',
    field: 'Proposed Development Summary',
    label: 'Proposed development summary',
  },
  { group: 'Operations', field: 'Operational Summary', label: 'Operational summary' },
  { group: 'Operations', field: 'Operating Schedule', label: 'Operating schedule' },
  { group: 'Construction', field: 'Construction Scope', label: 'Construction scope' },
  {
    group: 'Environmental analysis',
    field: 'Equipment / Emission Source Summary',
    label: 'Equipment / emission source summary',
  },
  {
    group: 'Sensitive receptors',
    field: 'Sensitive Receptor Summary',
    label: 'Sensitive receptor summary',
  },
  { group: 'Schedule', field: 'Target Completion Date', label: 'Target completion date' },
];

const overviewSections = [
  {
    label: 'Project',
    fields: [
      'Project Name',
      'Project Code',
      'Project Type',
      'Project Category',
      'Project Status',
      'Project Summary',
      'Project Description',
    ],
  },
  {
    label: 'Client & team',
    fields: [
      'Client',
      'Primary Client Contact',
      'Applicant',
      'Property Owner',
      'Operator / Tenant',
      'Lead Agency',
      'Jurisdiction',
      'Internal Project Manager',
      'Technical Leads',
      'External Partners / Subconsultants',
    ],
  },
];
const siteSections = [
  {
    label: 'Location & parcels',
    fields: [
      'Primary Project Address',
      'Additional Addresses',
      'Site Location Description',
      'City',
      'County',
      'State',
      'ZIP Code',
      'Latitude',
      'Longitude',
      'Assessor’s Parcel Numbers',
      'Site Area — Acres',
      'Site Area — Square Feet',
      'Existing Zoning',
      'Proposed Zoning',
      'General Plan Designation',
      'GIS / Map Link',
    ],
  },
  {
    label: 'Existing conditions',
    fields: [
      'Existing Use',
      'Existing Improvements Summary',
      'Adjacent Land Use Summary',
      'Known Site Constraints',
    ],
  },
  {
    label: 'Proposed development',
    fields: [
      'Proposed Use',
      'Proposed Development Summary',
      'Development Components Summary',
      'Demolition Scope',
      'Construction Scope',
      'Operational Summary',
      'Access and Circulation Summary',
      'Operating Schedule',
      'Opening / Operational Year',
    ],
  },
  {
    label: 'Environmental',
    fields: ['Equipment / Emission Source Summary', 'Sensitive Receptor Summary'],
  },
];
const workSections = [
  {
    label: 'Schedule & active work',
    fields: [
      'Project Start Date',
      'Target Completion Date',
      'Current Blockers',
      'Last Client Update',
    ],
  },
  {
    label: 'Reports & knowledge',
    fields: [
      'Reports Done',
      'Internal Notes / Tags',
      'Similar Projects',
      'Google Drive Report Link',
      'Project Closeout Summary',
      'Lessons / Reusable Findings',
      'Last Updated',
      'Updated By',
    ],
  },
];

function isEmpty(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && !value.trim()) ||
    (Array.isArray(value) && !value.length)
  );
}

export function ProjectWorkspace({
  onContext,
}: {
  onContext(value: Record<string, unknown>): void;
}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [project, setProject] = useState<PortalRecord>();
  const [documents, setDocuments] = useState<PortalRecord[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [provenance, setProvenance] = useState<ProvenanceItem[]>([]);
  const [pendingSuggestions, setPendingSuggestions] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [editingTab, setEditingTab] = useState<string>();
  const [activeTab, setActiveTab] = useState('overview');
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [documentId, setDocumentId] = useState<string>();
  const [briefOpen, setBriefOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [starredFlag, setStarredFlag] = useState(false);

  // The Command Palette and Alice slash commands open the upload dialog for
  // the current project via this event.
  useEffect(() => {
    const handler = () => setUploadOpen(true);
    window.addEventListener('ecorp:open-upload', handler);
    return () => window.removeEventListener('ecorp:open-upload', handler);
  }, []);

  async function load() {
    if (!id) return;
    setLoading(true);
    setError(undefined);
    try {
      const [item, events, prov] = await Promise.all([
        getRecord('projects', id),
        listActivity('project', id)
          .then((payload) => payload.items)
          .catch(() => [] as ActivityItem[]),
        getProvenance(id)
          .then((payload) => payload.items)
          .catch(() => [] as ProvenanceItem[]),
      ]);
      const docs = await listRecords(
        'documents',
        new URLSearchParams({
          pageSize: '100',
          project: id,
          sortField: 'Uploaded At',
          sortDirection: 'desc',
        }),
      ).catch(() => ({ items: [] as PortalRecord[] }));
      setProject(item.item);
      const projectDocuments = docs.items;
      setDocuments(projectDocuments);
      setActivity(events);
      setProvenance(prov);
      setStarredFlag(stars.has(id));
      const name = display(item.item.fields['Project Name']);
      recents.push(id, name || id);
      const needsReview = projectDocuments.filter(
        (doc) => display(doc.fields['Extraction Status']) === 'Needs Review',
      );
      let pending = 0;
      if (needsReview[0]) {
        const review = await getSuggestions(needsReview[0].id).catch(() => null);
        pending = review?.suggestions.filter((row) => row.review_status === 'pending').length ?? 0;
      }
      setPendingSuggestions(pending);
      onContext({
        route: 'project',
        resource: 'projects',
        projectId: id,
        projectName: name,
        pendingSuggestions: pending,
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load the project.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, [id]);

  const fields = project?.fields ?? {};
  const missing = useMemo(
    () => missingChecklist.filter((item) => isEmpty(fields[item.field])),
    [project],
  );
  const currentSitePlan = useMemo(
    () =>
      documents.find(
        (doc) =>
          display(doc.fields['Document Type']) === 'Site Plan' &&
          doc.fields['Current Version'] === true,
      ) ?? documents.find((doc) => display(doc.fields['Document Type']) === 'Site Plan'),
    [documents],
  );
  const provenanceByField = useMemo(() => {
    const map = new Map<string, ProvenanceItem>();
    for (const item of provenance) if (!map.has(item.field_key)) map.set(item.field_key, item);
    return map;
  }, [provenance]);

  const health = useMemo(() => {
    const warnings: string[] = [];
    if (isEmpty(fields.Client)) warnings.push('No client linked');
    if (isEmpty(fields['Primary Project Address'])) warnings.push('Missing address');
    if (!currentSitePlan) warnings.push('No current site plan');
    if (pendingSuggestions > 0) warnings.push(`${pendingSuggestions} unreviewed suggestions`);
    if (isEmpty(fields['Project Summary'])) warnings.push('Missing project summary');
    return warnings;
  }, [fields, currentSitePlan, pendingSuggestions]);

  async function copyBrief() {
    if (!id) return;
    setBriefOpen(true);
  }

  function beginEdit(tab: string) {
    setDraft(fields);
    setEditingTab(tab);
  }
  async function saveTab(sections: Array<{ label: string; fields: string[] }>) {
    setSaving(true);
    try {
      const names = new Set(sections.flatMap((section) => section.fields));
      const patch = Object.fromEntries(Object.entries(draft).filter(([name]) => names.has(name)));
      const result = await updateRecord('projects', project!.id, patch);
      setProject(result.item);
      setEditingTab(undefined);
      toast('Project updated.');
      void load();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Update failed.', 'error');
    } finally {
      setSaving(false);
    }
  }
  async function removeProject() {
    if (
      !window.confirm(
        `Delete ${display(fields['Project Name']) || 'this project'}? This cannot be undone.`,
      )
    )
      return;
    try {
      await deleteRecord('projects', project!.id);
      toast('Project deleted.');
      navigate('/projects');
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Delete failed.', 'error');
    }
  }

  const projectName = display(fields['Project Name']) || 'Untitled Project';
  const clientId = linkedIds(fields.Client)[0];
  usePageHeader(
    {
      title: (
        <span className="flex min-w-0 items-center gap-2">
          <Link
            to="/projects"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
            aria-label="Back to Projects"
            title="Back to Projects"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </Link>
          <span className="truncate">{projectName}</span>
          {project && (
            <button
              type="button"
              className="shrink-0 rounded-md p-1"
              aria-label={
                starredFlag ? 'Remove project from Quick Access' : 'Pin project to Quick Access'
              }
              onClick={() => {
                const next = stars.toggle(project.id, projectName);
                setStarredFlag(next);
                toast(
                  next ? 'Project pinned to Quick Access.' : 'Project removed from Quick Access.',
                );
              }}
            >
              <Star
                className={cn(
                  'h-5 w-5 transition',
                  starredFlag
                    ? 'fill-amber-400 text-amber-400'
                    : 'text-muted-foreground hover:text-amber-400',
                )}
              />
            </button>
          )}
        </span>
      ),
      subtitle: (
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          {display(fields['Project Code']) && (
            <span className="font-mono">{display(fields['Project Code'])}</span>
          )}
          {clientId ? (
            <Link
              className="hover:text-foreground hover:underline"
              to={`/clients?open=${clientId}`}
            >
              {display(fields.Client)}
            </Link>
          ) : (
            <span className="italic">No client linked</span>
          )}
          {display(fields['Project Type']) && <span>· {display(fields['Project Type'])}</span>}
          {display(fields.Jurisdiction) && <span>· {display(fields.Jurisdiction)}</span>}
        </span>
      ),
      context: project ? (
        <>
          <StatusPill value={fields['Project Status']} />
          {currentSitePlan ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
              onClick={() => setDocumentId(currentSitePlan.id)}
            >
              <FileStack className="h-3 w-3" aria-hidden />
              Current site plan: {display(currentSitePlan.fields['Document Name'])}
            </button>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-3 w-3" aria-hidden />
              No current site plan
            </span>
          )}
          {missing.length > 0 && (
            <span className="rounded-full border px-2 py-0.5">
              {missing.length} missing item{missing.length === 1 ? '' : 's'}
            </span>
          )}
          {pendingSuggestions > 0 && (
            <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 font-medium text-primary">
              {pendingSuggestions} suggestions to review
            </span>
          )}
        </>
      ) : undefined,
      actions: project
        ? [
            {
              id: 'upload-document',
              label: 'Upload Document',
              icon: <Upload className="h-4 w-4" aria-hidden />,
              variant: 'primary',
              onClick: () => setUploadOpen(true),
            },
            {
              id: 'project-brief',
              label: 'Project Brief',
              icon: <ClipboardCopy className="h-4 w-4" aria-hidden />,
              variant: 'secondary',
              onClick: () => void copyBrief(),
            },
            {
              id: 'delete-project',
              label: 'Delete Project',
              icon: <Trash2 className="h-4 w-4" aria-hidden />,
              variant: 'destructive',
              overflow: true,
              onClick: () => void removeProject(),
            },
          ]
        : undefined,
    },
    [
      project,
      projectName,
      clientId,
      currentSitePlan,
      missing.length,
      pendingSuggestions,
      starredFlag,
    ],
  );

  if (loading)
    return (
      <div className="flex items-center gap-2 p-10 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading project…
      </div>
    );
  if (error || !project)
    return (
      <div className="space-y-3 p-4">
        <ErrorNotice message={error ?? 'Project not found.'} onRetry={() => void load()} />
        <Button variant="outline" onClick={() => navigate('/projects')}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back to Projects
        </Button>
      </div>
    );

  const mapQuery = [
    display(fields['Primary Project Address']),
    display(fields.City),
    display(fields.State),
  ]
    .filter(Boolean)
    .join(', ');
  const activeSections =
    activeTab === 'overview'
      ? overviewSections
      : activeTab === 'site'
        ? siteSections
        : activeTab === 'work'
          ? workSections
          : undefined;

  return (
    <div className="space-y-5">
      <div className="grid gap-5 xl:grid-cols-[1fr_310px]">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="site">Site & Development</TabsTrigger>
              <TabsTrigger value="work">Work & History</TabsTrigger>
              <TabsTrigger value="documents">
                Documents
                {documents.length > 0 && (
                  <span className="ml-1.5 rounded-full bg-muted px-1.5 text-[11px] tabular-nums">
                    {documents.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
            </TabsList>
            {activeSections && (
              <TabEditActions
                editing={editingTab === activeTab}
                saving={saving}
                onEdit={() => beginEdit(activeTab)}
                onCancel={() => setEditingTab(undefined)}
                onSave={() => void saveTab(activeSections)}
              />
            )}
          </div>

          {/* ---- Overview ---- */}
          <TabsContent value="overview" className="space-y-4 pt-4">
            <ProjectTabEditor
              fields={fields}
              draft={draft}
              editing={editingTab === 'overview'}
              sections={overviewSections}
              provenance={provenanceByField}
              onDraft={setDraft}
            />
            <section className="rounded-xl border bg-card p-4">
              <SectionHeader title="Data health">
                {health.length === 0 && (
                  <span className="text-xs text-emerald-600 dark:text-emerald-400">
                    All checks pass
                  </span>
                )}
              </SectionHeader>
              {health.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  This project record looks complete and current.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {health.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <MissingInfoPanel missing={missing} projectName={display(fields['Project Name'])} />
          </TabsContent>

          {/* ---- Site & Development ---- */}
          <TabsContent value="site" className="space-y-4 pt-4">
            {mapQuery && (
              <div className="flex justify-end">
                <a
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <MapIcon className="h-3.5 w-3.5" />
                  Open map
                </a>
              </div>
            )}
            <ProjectTabEditor
              fields={fields}
              draft={draft}
              editing={editingTab === 'site'}
              sections={siteSections}
              provenance={provenanceByField}
              onDraft={setDraft}
            />
          </TabsContent>

          <TabsContent value="work" className="space-y-4 pt-4">
            <ProjectTabEditor
              fields={fields}
              draft={draft}
              editing={editingTab === 'work'}
              sections={workSections}
              provenance={provenanceByField}
              onDraft={setDraft}
            />
          </TabsContent>

          {/* ---- Documents ---- */}
          <TabsContent value="documents" className="space-y-4 pt-4">
            {documents.length === 0 ? (
              <EmptyState
                title="No documents yet."
                hint="Upload a site plan or another project file to start building the record."
                action={
                  <Button onClick={() => setUploadOpen(true)}>
                    <Upload className="mr-1.5 h-4 w-4" />
                    Upload Document
                  </Button>
                }
              />
            ) : (
              <RecordTable
                items={documents}
                resource={ecorpResources.documents}
                onSelect={(item) => setDocumentId(item.id)}
              />
            )}
          </TabsContent>

          {/* ---- Activity ---- */}
          <TabsContent value="activity" className="pt-4">
            {activity.length === 0 ? (
              <EmptyState
                title="No activity recorded yet."
                hint="Uploads, extractions, and field approvals will appear here."
              />
            ) : (
              <ol className="space-y-2">
                {activity.map((item) => (
                  <li key={item.id} className="flex gap-3 rounded-xl border bg-card p-3 text-sm">
                    <ActivityIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <div className="min-w-0 flex-1">
                      <p>{item.summary ?? item.notes ?? item.action}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {item.actor_name ?? item.actor_email ?? 'System'} ·{' '}
                        {relativeTime(item.created_at)}
                        {item.category ? ` · ${item.category}` : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </TabsContent>
        </Tabs>

        {/* ---- Companion panel ---- */}
        <aside className="hidden space-y-3 xl:block">
          <div className="rounded-xl border bg-card p-4">
            <SectionHeader title="Companion" />
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Current blockers
                </p>
                <p className="mt-0.5">
                  {display(fields['Current Blockers']) || (
                    <span className="text-muted-foreground">None</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Current site plan
                </p>
                {currentSitePlan ? (
                  <button
                    className="mt-0.5 text-left text-primary hover:underline"
                    onClick={() => setDocumentId(currentSitePlan.id)}
                  >
                    {display(currentSitePlan.fields['Document Name'])}
                  </button>
                ) : (
                  <p className="mt-0.5 text-muted-foreground">None uploaded</p>
                )}
              </div>
              {pendingSuggestions > 0 && (
                <p className="rounded-lg border border-primary/40 bg-primary/5 px-2.5 py-1.5 text-xs text-primary">
                  {pendingSuggestions} extraction suggestion{pendingSuggestions === 1 ? '' : 's'}{' '}
                  awaiting review.
                </p>
              )}
            </div>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <SectionHeader title="Recent documents" />
            {documents.slice(0, 5).map((doc) => (
              <button
                key={doc.id}
                className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-muted"
                onClick={() => setDocumentId(doc.id)}
              >
                <span className="truncate">{display(doc.fields['Document Name'])}</span>
                <StatusPill value={doc.fields['Processing Status']} />
              </button>
            ))}
            {documents.length === 0 && (
              <p className="text-sm text-muted-foreground">Nothing uploaded yet.</p>
            )}
          </div>
        </aside>
      </div>

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        projectId={project.id}
        onUploaded={() => void load()}
      />
      <DocumentDetail
        documentId={documentId}
        open={Boolean(documentId)}
        onClose={() => setDocumentId(undefined)}
        onChanged={() => void load()}
      />
      <BriefDialog open={briefOpen} onClose={() => setBriefOpen(false)} projectId={project.id} />
    </div>
  );
}

function FieldGrid({
  fields,
  names,
  provenance,
  wide,
}: {
  fields: Record<string, unknown>;
  names: string[];
  provenance: Map<
    string,
    {
      source_label: string | null;
      source_sheet: string | null;
      source_page: number | null;
      applied_by_name: string | null;
      applied_at: string;
      previous_value: unknown;
    }
  >;
  wide?: boolean;
}) {
  return (
    <dl className={cn('grid gap-3', wide ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3')}>
      {names.map((name) => {
        const source = provenance.get(name);
        return (
          <div
            key={name}
            className={cn(
              wide && typeof fields[name] === 'string' && (fields[name] as string).length > 120
                ? 'sm:col-span-2'
                : undefined,
            )}
          >
            <dt className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {name}
              {source && (
                <span className="group relative inline-flex">
                  <Info className="h-3 w-3 cursor-help text-primary/70" />
                  <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1 hidden w-64 -translate-x-1/2 rounded-lg border bg-popover p-2 text-[11px] font-normal normal-case tracking-normal text-popover-foreground shadow-lg group-hover:block">
                    <span className="block font-medium">
                      {source.source_label ?? 'Reviewed extraction'}
                    </span>
                    {(source.source_sheet || source.source_page !== null) && (
                      <span className="block text-muted-foreground">
                        {source.source_sheet ? `Sheet ${source.source_sheet}` : ''}
                        {source.source_page !== null ? ` · p. ${source.source_page}` : ''}
                      </span>
                    )}
                    <span className="block text-muted-foreground">
                      Applied by {source.applied_by_name ?? 'unknown'} ·{' '}
                      {relativeTime(source.applied_at)}
                    </span>
                    {!isEmpty(source.previous_value) && (
                      <span className="block text-muted-foreground">
                        Previously: {display(source.previous_value)}
                      </span>
                    )}
                  </span>
                </span>
              )}
            </dt>
            <dd className="mt-0.5 whitespace-pre-wrap break-words text-sm">
              {display(fields[name]) || <span className="text-muted-foreground">—</span>}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function TabEditActions({
  editing,
  saving,
  onEdit,
  onCancel,
  onSave,
}: {
  editing: boolean;
  saving: boolean;
  onEdit(): void;
  onCancel(): void;
  onSave(): void;
}) {
  return (
    <div className="flex gap-2">
      {editing ? (
        <>
          <Button size="sm" variant="outline" disabled={saving} onClick={onCancel}>
            <X className="mr-1.5 h-4 w-4" />
            Cancel
          </Button>
          <Button size="sm" disabled={saving} onClick={onSave}>
            {saving ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-1.5 h-4 w-4" />
            )}
            Save changes
          </Button>
        </>
      ) : (
        <Button size="sm" variant="outline" onClick={onEdit}>
          <Pencil className="mr-1.5 h-4 w-4" />
          Edit this tab
        </Button>
      )}
    </div>
  );
}

function ProjectTabEditor({
  fields,
  draft,
  editing,
  sections,
  provenance,
  onDraft,
}: {
  fields: Record<string, unknown>;
  draft: Record<string, unknown>;
  editing: boolean;
  sections: Array<{ label: string; fields: string[] }>;
  provenance: Map<string, ProvenanceItem>;
  onDraft(value: Record<string, unknown>): void;
}) {
  const definitions = new Map(ecorpResources.projects.fields.map((field) => [field.id, field]));
  return (
    <>
      {sections.map((section) => (
        <section key={section.label} className="rounded-xl border bg-card p-4">
          <SectionHeader title={section.label} />
          {editing ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {section.fields.map((name) => {
                const definition = definitions.get(name);
                if (!definition || definition.readonly)
                  return (
                    <div key={name}>
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {name}
                      </p>
                      <p className="mt-1 text-sm">{display(draft[name]) || '—'}</p>
                    </div>
                  );
                return (
                  <FieldInput
                    key={name}
                    definition={definition}
                    value={draft[name]}
                    onChange={(value) => onDraft({ ...draft, [name]: value })}
                  />
                );
              })}
            </div>
          ) : (
            <FieldGrid fields={fields} names={section.fields} provenance={provenance} wide />
          )}
        </section>
      ))}
    </>
  );
}

function MissingInfoPanel({
  missing,
  projectName,
}: {
  missing: Array<{ group: string; label: string }>;
  projectName: string;
}) {
  const { toast } = useToast();
  if (!missing.length) return null;
  const groups = new Map<string, string[]>();
  for (const item of missing) {
    const list = groups.get(item.group) ?? [];
    list.push(item.label);
    groups.set(item.group, list);
  }
  function copyRequest() {
    const lines = [
      `Hi — to keep ${projectName || 'the project'} moving, could you send over the following?`,
      '',
    ];
    for (const [group, labels] of groups) {
      lines.push(`${group}:`);
      for (const label of labels) lines.push(`  • ${label}`);
    }
    void navigator.clipboard
      .writeText(lines.join('\n'))
      .then(() => toast('Client follow-up request copied.'));
  }
  return (
    <section className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
      <SectionHeader title={`Missing information (${missing.length})`}>
        <Button size="sm" variant="outline" onClick={copyRequest}>
          <ClipboardCopy className="mr-1.5 h-3.5 w-3.5" />
          Copy as client request
        </Button>
      </SectionHeader>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[...groups.entries()].map(([group, labels]) => (
          <div key={group}>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {group}
            </p>
            <ul className="mt-1 space-y-0.5 text-sm">
              {labels.map((label) => (
                <li key={label} className="flex items-center gap-1.5">
                  <span className="h-1 w-1 rounded-full bg-amber-500" />
                  {label}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export function BriefDialog({
  open,
  onClose,
  projectId,
}: {
  open: boolean;
  onClose(): void;
  projectId?: string;
}) {
  const { toast } = useToast();
  const [brief, setBrief] = useState<string>();
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !projectId) return;
    setLoading(true);
    setBrief(undefined);
    void getProjectBrief(projectId)
      .then((payload) => setBrief(payload.brief))
      .catch((cause) =>
        setBrief(
          `Could not generate the brief: ${cause instanceof Error ? cause.message : 'unknown error'}`,
        ),
      )
      .finally(() => setLoading(false));
  }, [open, projectId]);
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Project Brief</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Generating brief…
          </div>
        ) : (
          <>
            <pre className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap rounded-xl border bg-muted/40 p-4 font-sans text-sm">
              {brief}
            </pre>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  if (brief)
                    void navigator.clipboard
                      .writeText(brief)
                      .then(() => toast('Project brief copied.'));
                }}
              >
                <ClipboardCopy className="mr-1.5 h-4 w-4" />
                Copy brief
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
